import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { OPENING_HOURS_SPEC } from "../lib/business";
import { useSeoOverride } from "../lib/seoOverrides";

export const SITE_NAME = "Cloud Accounting Support Services";
export const BRAND_SHORT = "CASS";
export const withBrand = (title) => {
  if (/\|\s*(CASS|Cloud Accounting Support Services)\s*$/i.test(title)) return title;
  const full = `${title} | ${SITE_NAME}`;
  return full.length <= 60 ? full : `${title} | ${BRAND_SHORT}`;
};
export const DEFAULT_TITLE = `${SITE_NAME} | More Than Numbers`;
export const DEFAULT_DESC =
  "Cloud Accounting Support Services (CASS) is a Xero Platinum Partner firm offering bookkeeping, payroll, VAT, tax and full cloud accounting support — more than numbers.";

// Google truncates the SERP snippet at roughly 160 characters. 79 of 145 pages
// were over that (up to 300), so trim on a sentence boundary where one is close
// enough, otherwise on a word boundary. The longer copy is kept for OG/Twitter.
export const clampDesc = (text, max = 160) => {
  const s = (text || "").replace(/\s+/g, " ").trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const sentence = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("! "), cut.lastIndexOf("? "));
  if (sentence >= max * 0.6) return cut.slice(0, sentence + 1);
  return cut.slice(0, cut.lastIndexOf(" ")).replace(/[,;:—-]+$/, "") + "…";
};

// Social profiles (Google Knowledge Panel sameAs signal)
export const SOCIALS = [
  "https://www.facebook.com/cloudaccountingsupportservices/",
  "https://www.linkedin.com/company/cass-online/",
  "https://www.youtube.com/channel/UC4MoxGJbIaCqiDazmRvpwoQ",
];

// Preferred canonical origin — always emit the production host in canonical/OG/
// schema URLs so www, preview and trailing-slash variants consolidate to one.
export const CANONICAL_ORIGIN = "https://cassonline.co.uk";
const origin = () => CANONICAL_ORIGIN;
export const logoUrl = () => `${origin()}/logo.png`;
export const defaultOgImage = () => `${origin()}/og-image.jpg`;

// Build the founder Person node. `f` is an optional normalized object
// { name, role, imageUrl, description, sameAs[] } sourced from the homepage CMS.
const founderPerson = (f) => {
  const person = {
    "@type": "Person",
    name: (f && f.name) || "Paul Barnes",
    jobTitle: (f && f.role) || "Founder",
    worksFor: { "@id": `${origin()}/#organization` },
  };
  if (f) {
    if (f.imageUrl) person.image = f.imageUrl;
    if (f.description) person.description = f.description;
    if (Array.isArray(f.sameAs) && f.sameAs.length) person.sameAs = f.sameAs;
  }
  return person;
};

// Global Organization / local business schema.
// NOTE: aggregateRating is intentionally NOT included. Self-serving review
// snippets are against Google's guidelines and caused a "multiple aggregate
// ratings" error. Visible Google reviews/stars remain on the site separately.
export const organizationSchema = (founder) => ({
  "@context": "https://schema.org",
  "@type": "AccountingService",
  "@id": `${origin()}/#organization`,
  name: SITE_NAME,
  legalName: "Cloud Accounting Support Services Ltd",
  alternateName: "CASS",
  description: DEFAULT_DESC,
  url: origin(),
  logo: logoUrl(),
  image: defaultOgImage(),
  telephone: "+44 1827 780550",
  email: "info@cass-online.co.uk",
  // Both already published in the footer. In the markup they tie the website to
  // the registered company, which is what a search engine or an AI assistant
  // needs to be sure this CASS is that CASS. schema.org has no company-number
  // field, so the Companies House number goes in `identifier` as a named
  // PropertyValue — the convention Google documents for registration numbers.
  identifier: {
    "@type": "PropertyValue",
    name: "Companies House company number",
    value: "10718230",
  },
  vatID: "287 2656 61",
  taxID: "287 2656 61",
  address: {
    "@type": "PostalAddress",
    streetAddress: "3 Pebble Close, Amington",
    addressLocality: "Tamworth",
    addressRegion: "Staffordshire",
    postalCode: "B77 4RD",
    addressCountry: "GB",
  },
  areaServed: ["Tamworth", "Sutton Coldfield", "Burton-upon-Trent", "Birmingham", "West Midlands", "Staffordshire", "United Kingdom"],
  // Office coordinates for B77 4RD (source: postcodes.io, ONS data).
  geo: { "@type": "GeoCoordinates", latitude: 52.628663, longitude: -1.648328 },
  priceRange: "££",
  openingHoursSpecification: OPENING_HOURS_SPEC,
  sameAs: SOCIALS,
  founder: founderPerson(founder),
});

// WebSite schema (helps Google understand the site entity)
export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${origin()}/#website`,
  name: SITE_NAME,
  url: origin(),
  publisher: { "@id": `${origin()}/#organization` },
});

// BreadcrumbList schema — items: [{ name, path }]
export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${origin()}${it.path}`,
  })),
});

// FAQPage markup is deliberately not emitted. Google discontinued the FAQ rich
// result for sites like this one, so the questions stay visible on the page and
// the markup goes. Callers can keep calling this; it adds nothing to the head.
export const faqSchema = () => null;

// Article schema for a client success story / case study.
// `image` and `url` should be absolute URLs.
export const caseStudySchema = (item, { image, url }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: (item.title || "").slice(0, 110),
  description: item.summary || item.title || "",
  ...(image ? { image: [image] } : {}),
  ...(Array.isArray(item.content) && item.content.length ? { articleBody: item.content.join("\n\n") } : {}),
  ...(item.industry ? { about: item.industry } : {}),
  ...(item.created_at ? { datePublished: item.created_at } : {}),
  author: { "@type": "Organization", name: SITE_NAME, "@id": `${origin()}/#organization` },
  publisher: { "@id": `${origin()}/#organization` },
  mainEntityOfPage: { "@type": "WebPage", "@id": url },
  isPartOf: { "@id": `${origin()}/#website` },
});

export default function Seo({ title, description, image, type = "website", noindex = false, jsonLd, canonical, ogTitle, ogDescription }) {
  const { pathname } = useLocation();
  // Normalise the trailing slash: /faqs/ and /faqs are the same page, and the
  // edge serves both with a 200. Without this each slash variant emitted a
  // self-canonical and duplicated every URL on the site (SEO fix A1-5).
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  // An admin override wins, where one has been filled in for this URL.
  const ov = useSeoOverride(path) || {};
  if (ov.seo_title) title = ov.seo_title;
  if (ov.meta_description) description = ov.meta_description;
  if (ov.canonical) canonical = ov.canonical;
  if (ov.og_image) image = ov.og_image;
  if (ov.noindex === true) noindex = true;
  const url = origin() + path;
  const canonicalUrl = canonical ? (canonical.startsWith("http") ? canonical : origin() + canonical) : url;
  // The full company name is 33 characters, which left only ~26 for the page
  // itself and pushed 77 of 145 titles past the ~65 characters Google will show.
  // Use the full name where it fits, otherwise the brand short form. Titles that
  // already carry the brand (some case studies) are left alone rather than
  // having it appended twice.
  const fullTitle = title ? withBrand(title) : DEFAULT_TITLE;
  const desc = (description || DEFAULT_DESC).replace(/\s+/g, " ").trim().slice(0, 300);
  const metaDesc = clampDesc(desc);
  const ogTitleFull = ogTitle ? `${ogTitle}` : fullTitle;
  const ogDesc = (ogDescription || desc).replace(/\s+/g, " ").trim().slice(0, 300);
  // Every page points its preview image at the one card address, handing over
  // its own photograph when it has one. The server decides what to serve —
  // generated card, the page's own picture, an override or the site default —
  // so the admin's switches apply straight away, with no rebuild.
  const own = image ? (image.startsWith("http") ? image : origin() + image) : "";
  const img = `${origin()}/api/share-card.png?path=${encodeURIComponent(path)}${own ? `&own=${encodeURIComponent(own)}` : ""}`;
  const blocks = (jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []).filter(Boolean);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDesc} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={ogTitleFull} />
      <meta property="og:description" content={ogDesc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content="en_GB" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitleFull} />
      <meta name="twitter:description" content={ogDesc} />
      <meta name="twitter:image" content={img} />

      {blocks.map((b, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(b)}</script>
      ))}
    </Helmet>
  );
}
